import { useEffect, useMemo, useState } from 'react'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'
import CalendarFallbackIcon from '@components/shared/CalendarFallbackIcon'
import './DeadlineCounterPage.css'

const PROCESS_TYPES = [
  ['ELECTRONIC', 'Eletrônico'],
  ['PHYSICAL', 'Físico'],
]
const ELECTRONIC_SYSTEMS = ['PJe', 'e-SAJ', 'Eproc', 'Projudi', 'SAJ-SG', 'Outro']
const INSTANCES = [
  ['FIRST', '1ª instância'],
  ['SECOND', '2ª instância'],
  ['SUPERIOR', 'Tribunal superior'],
]

function localIsoDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const INITIAL_FORM = {
  amount: 15,
  legalRegime: 'CPC_2015',
  eventDate: localIsoDate(),
  state: 'AP',
  court: 'TJAP',
  processType: 'ELECTRONIC',
  electronicSystem: 'PJe',
  instance: 'FIRST',
  includeMunicipalSuspensions: false,
  city: '',
}

const ROLE_LABELS = {
  PRIMARY_CALENDAR: 'Calendário principal',
  LOCAL_HOLIDAYS: 'Feriados locais',
  DEADLINE_SUSPENSIONS: 'Suspensões de prazo',
  COURT_CALENDAR: 'Calendário do tribunal',
  AMENDMENTS_WATCHER: 'Atualizações e comunicados',
}

const DAY_STATUS = {
  COUNTED: ['Contado', 'is-counted'],
  WEEKEND: ['Fim de semana', 'is-weekend'],
  JUDICIAL_NON_BUSINESS_DAY: ['Não útil no calendário', 'is-calendar'],
}

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim()
}

function buildCourtOptions(data) {
  if (data.courts?.length) {
    return data.courts.map((court) => ({
      ...court,
      states: court.states || [],
    }))
  }

  const courts = new Map()
  const states = data.states || []
  states.forEach((state) => {
    const stateCourts = state.courts || []
    stateCourts.forEach((court) => {
      const current = courts.get(court.code)
      if (current) {
        if (!current.states.includes(state.code)) current.states.push(state.code)
        return
      }
      courts.set(court.code, { ...court, states: [state.code] })
    })
  })
  return Array.from(courts.values())
}

function formatDate(value, options = { dateStyle: 'long' }) {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return value
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR', options)
}

function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`deadline-counter-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  )
}

function CalendarSources({ court }) {
  if (!court) {
    return <p className="deadline-counter-empty">Selecione uma UF e um tribunal para ver o calendário aplicável.</p>
  }

  return (
    <div className="deadline-counter-sources">
      <div className="deadline-counter-calendar-heading">
        <span className="deadline-counter-eyebrow">Calendário judicial</span>
        <h3>{court.name}</h3>
        <p>Fontes oficiais catalogadas para este tribunal. O contador aplica feriados nacionais e os eventos vigentes do tribunal quando disponíveis.</p>
      </div>
      <div className="deadline-counter-source-list">
        {court.sources?.map((source) => (
          <article className="deadline-counter-source" key={source.key}>
            <div>
              <strong>{ROLE_LABELS[source.role] || 'Calendário judicial'}</strong>
              <span>{source.supportsLocalities ? 'Inclui dados por município ou comarca' : 'Abrangência do tribunal'}</span>
            </div>
            <a href={source.discoveryUrl} target="_blank" rel="noreferrer" aria-label={`Abrir fonte oficial ${source.key}`}>
              Fonte oficial <i className="fas fa-arrow-up-right-from-square" />
            </a>
          </article>
        ))}
      </div>
    </div>
  )
}

export function CounterResult({ result }) {
  if (!result) return null

  const counted = result.timeline?.filter((day) => day.status === 'COUNTED').length || 0
  const skipped = (result.timeline?.length || 0) - counted

  if (result.requiresHumanReview) {
    return (
      <section className="deadline-counter-result is-review" aria-live="polite">
        <div className="deadline-counter-result-icon"><i className="fas fa-triangle-exclamation" /></div>
        <div>
          <span className="deadline-counter-eyebrow">Revisão necessária</span>
          <h2>Não foi possível confirmar o vencimento</h2>
          <p>{result.reviewReason}</p>
        </div>
      </section>
    )
  }

  return (
    <section className="deadline-counter-result" aria-live="polite">
      <div className="deadline-counter-result-main">
        <span className="deadline-counter-eyebrow">Vencimento calculado</span>
        <strong>{formatDate(result.dueDate)}{result.dueDate && <CalendarFallbackIcon source={result} />}</strong>
        <p>{result.amount} dias úteis no Novo CPC, com início da contagem em {formatDate(result.firstCountedDate)}.</p>
      </div>
      <div className="deadline-counter-result-stats">
        <div><strong>{counted}</strong><span>dias contados</span></div>
        <div><strong>{skipped}</strong><span>dias desconsiderados</span></div>
        <div><strong>{result.appliedScopes?.length || 0}</strong><span>escopos de calendário</span></div>
      </div>
      <details className="deadline-counter-timeline">
        <summary>Ver memória de cálculo</summary>
        <div className="deadline-counter-timeline-grid">
          {result.timeline?.map((day) => {
            const [label, className] = DAY_STATUS[day.status] || [day.status, '']
            return (
              <div className={`deadline-counter-day ${className}`} key={day.date}>
                <strong>{formatDate(day.date, { day: '2-digit', month: 'short' })}</strong>
                <span>{day.ordinal ? `${day.ordinal}º dia` : label}</span>
              </div>
            )
          })}
        </div>
        <div className="deadline-counter-version">
          <span>Calendário: {result.appliedScopes?.join(' · ') || 'fins de semana'}</span>
          {result.calendarVersion ? <code>{result.calendarVersion}</code> : null}
        </div>
      </details>
    </section>
  )
}

export default function DeadlineCounterPage() {
  const { showError } = useNotification()
  const [form, setForm] = useState(INITIAL_FORM)
  const [options, setOptions] = useState({ legalRegimes: [], states: [], courts: [] })
  const [loadingOptions, setLoadingOptions] = useState(true)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [courtSearch, setCourtSearch] = useState('')
  const [courtListOpen, setCourtListOpen] = useState(false)
  const [activeCourtIndex, setActiveCourtIndex] = useState(0)

  useEffect(() => {
    let active = true

    const loadOptions = async () => {
      const response = await ApiService.getDeadlineCounterOptions()
      if (!active) return
      setLoadingOptions(false)
      if (!response.success) {
        showError(response.error || 'Não foi possível carregar os tribunais e calendários.')
        return
      }

      const data = response.data || {}
      const states = data.states || []
      const courts = buildCourtOptions(data)
      setOptions({ legalRegimes: data.legalRegimes || [], states, courts })
      setForm((current) => {
        const state = states.find((item) => item.code === current.state) || states[0]
        const court = courts.find((item) => item.code === current.court && item.states.includes(state?.code))
          || courts.find((item) => item.states.includes(state?.code))
        return { ...current, state: state?.code || '', court: court?.code || '' }
      })
    }

    loadOptions()
    return () => { active = false }
  }, [showError])

  const selectedCourt = useMemo(
    () => options.courts.find((item) => item.code === form.court),
    [form.court, options.courts],
  )
  const filteredCourts = useMemo(() => {
    const query = normalizeSearch(courtSearch)
    const queryIsSelectedCourt = selectedCourt
      && (normalizeSearch(selectedCourt.code) === query || normalizeSearch(selectedCourt.name) === query)
    if (!query || (courtListOpen && queryIsSelectedCourt)) return options.courts
    return options.courts.filter((court) => normalizeSearch(
      `${court.code} ${court.name} ${(court.states || []).join(' ')}`,
    ).includes(query))
  }, [courtListOpen, courtSearch, options.courts, selectedCourt])

  useEffect(() => {
    setCourtSearch(selectedCourt?.name || '')
  }, [selectedCourt])

  const handleField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    setResult(null)
    setForm((current) => ({ ...current, [field]: value }))
  }

  const handleState = (event) => {
    const state = options.states.find((item) => item.code === event.target.value)
    const court = options.courts.find((item) => item.states.includes(state?.code))
    setResult(null)
    setForm((current) => ({ ...current, state: state?.code || '', court: court?.code || '' }))
  }

  const selectCourt = (court) => {
    const courtStates = (court.states || []).filter((state) => state !== 'BR')
    const state = courtStates.includes(form.state) ? form.state : courtStates[0]
    setResult(null)
    setForm((current) => ({ ...current, court: court.code, state: state || current.state }))
    setCourtSearch(court.name)
    setCourtListOpen(false)
    setActiveCourtIndex(0)
  }

  const handleCourtSearch = (event) => {
    setCourtSearch(event.target.value)
    setCourtListOpen(true)
    setActiveCourtIndex(0)
    setResult(null)
  }

  const handleCourtKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCourtListOpen(true)
      setActiveCourtIndex((current) => Math.min(current + 1, Math.max(filteredCourts.length - 1, 0)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveCourtIndex((current) => Math.max(current - 1, 0))
    } else if (event.key === 'Enter' && courtListOpen && filteredCourts[activeCourtIndex]) {
      event.preventDefault()
      selectCourt(filteredCourts[activeCourtIndex])
    } else if (event.key === 'Escape') {
      setCourtListOpen(false)
      setCourtSearch(selectedCourt?.name || '')
    }
  }

  const handleCourtBlur = () => {
    const exactCourt = options.courts.find((court) => {
      const query = normalizeSearch(courtSearch)
      return normalizeSearch(court.code) === query || normalizeSearch(court.name) === query
    })
    if (exactCourt) selectCourt(exactCourt)
    else setCourtSearch(selectedCourt?.name || '')
    setCourtListOpen(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!form.eventDate || !form.state || !form.court) {
      showError('Preencha a data do evento, a UF e o tribunal.')
      return
    }
    if (form.processType === 'ELECTRONIC' && !form.electronicSystem) {
      showError('Selecione o sistema do processo eletrônico.')
      return
    }
    if (form.includeMunicipalSuspensions && !form.city.trim()) {
      showError('Informe o município para incluir suspensões municipais.')
      return
    }

    setLoading(true)
    const response = await ApiService.countDeadline({
      ...form,
      amount: Number(form.amount),
      city: form.includeMunicipalSuspensions ? form.city.trim() : null,
      electronicSystem: form.processType === 'ELECTRONIC' ? form.electronicSystem : null,
    })
    setLoading(false)

    if (!response.success) {
      showError(response.error || 'Não foi possível calcular o prazo.')
      return
    }
    setResult(response.data)
  }

  return (
    <div className="main-content deadline-counter-page">
      <div className="deadline-counter-layout">
        <form className="deadline-counter-card" onSubmit={handleSubmit}>
          <div className="deadline-counter-intro">
            <span className="deadline-counter-eyebrow">Contagem por UF</span>
            <h2>Calcule o prazo processual</h2>
            <p>Informe o dia do começo e o contexto do processo. O vencimento usa o calendário judicial do tribunal selecionado.</p>
          </div>

          <div className="deadline-counter-sentence">
            <span>Prazo de</span>
            <Field label="Quantidade" className="is-compact">
              <input type="number" min="1" max="365" value={form.amount} onChange={handleField('amount')} required />
            </Field>
            <span>dias no</span>
            <Field label="Regime" className="is-wide">
              <select value={form.legalRegime} onChange={handleField('legalRegime')} disabled={loadingOptions}>
                {(options.legalRegimes.length ? options.legalRegimes : [{ code: 'CPC_2015', label: 'Novo CPC — dias úteis' }]).map((item) => (
                  <option value={item.code} key={item.code}>{item.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="deadline-counter-grid two-columns">
            <Field label="Data do evento" hint="O dia informado é excluído da contagem.">
              <input type="date" value={form.eventDate} onChange={handleField('eventDate')} required />
            </Field>
            <Field label="UF">
              <select value={form.state} onChange={handleState} disabled={loadingOptions} required>
                {options.states.map((state) => <option value={state.code} key={state.code}>{state.code}</option>)}
              </select>
            </Field>
            <div className="deadline-counter-field">
              <span id="deadline-counter-court-label">Tribunal</span>
              <div className="deadline-counter-combobox">
                <div className="deadline-counter-combobox-input">
                  <i className="fas fa-magnifying-glass" aria-hidden="true" />
                  <input
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-labelledby="deadline-counter-court-label"
                    aria-expanded={courtListOpen}
                    aria-controls="deadline-counter-court-options"
                    aria-activedescendant={courtListOpen && filteredCourts[activeCourtIndex]
                      ? `deadline-counter-court-${filteredCourts[activeCourtIndex].code}`
                      : undefined}
                    value={courtSearch}
                    onChange={handleCourtSearch}
                    onFocus={(event) => {
                      event.target.select()
                      setCourtListOpen(true)
                    }}
                    onBlur={handleCourtBlur}
                    onKeyDown={handleCourtKeyDown}
                    placeholder="Digite o tribunal ou a UF"
                    disabled={loadingOptions}
                    required
                  />
                  <i className="fas fa-chevron-down" aria-hidden="true" />
                </div>
                {courtListOpen ? (
                  <div className="deadline-counter-combobox-list" id="deadline-counter-court-options" role="listbox">
                    {filteredCourts.length ? filteredCourts.map((court, index) => (
                      <button
                        type="button"
                        id={`deadline-counter-court-${court.code}`}
                        className={index === activeCourtIndex ? 'is-active' : ''}
                        role="option"
                        aria-selected={court.code === form.court}
                        key={court.code}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectCourt(court)}
                      >
                        <strong>{court.name}</strong>
                        <span>{court.states?.join(' · ') || 'Abrangência nacional'}</span>
                      </button>
                    )) : (
                      <p>Nenhum tribunal encontrado.</p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
            <Field label="Tipo do processo">
              <select value={form.processType} onChange={handleField('processType')}>
                {PROCESS_TYPES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
              </select>
            </Field>
            <Field label="Sistema eletrônico" hint="Contexto informativo; não altera o marco informado.">
              <select value={form.electronicSystem} onChange={handleField('electronicSystem')} disabled={form.processType !== 'ELECTRONIC'}>
                <option value="">Selecionar...</option>
                {ELECTRONIC_SYSTEMS.map((system) => <option value={system} key={system}>{system}</option>)}
              </select>
            </Field>
            <Field label="Instância">
              <select value={form.instance} onChange={handleField('instance')}>
                {INSTANCES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
              </select>
            </Field>
          </div>

          <div className="deadline-counter-municipal">
            <label className="deadline-counter-switch">
              <input
                type="checkbox"
                checked={form.includeMunicipalSuspensions}
                onChange={handleField('includeMunicipalSuspensions')}
              />
              <span aria-hidden="true" />
              <strong>Incluir suspensões municipais</strong>
            </label>
            {form.includeMunicipalSuspensions ? (
              <Field label="Município" className="deadline-counter-city">
                <input value={form.city} onChange={handleField('city')} placeholder="Ex.: Macapá" required />
              </Field>
            ) : <small>Por padrão, o contador usa apenas os escopos nacional, estadual e do tribunal.</small>}
          </div>

          <div className="deadline-counter-action-row">
            <div className="deadline-counter-legal-note">
              <i className="fas fa-scale-balanced" />
              <span>Confirme se a data informada é o marco jurídico correto para o caso concreto.</span>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading || loadingOptions}>
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-stopwatch'}`} />
              {loading ? 'Calculando...' : 'Calcular prazo'}
            </button>
          </div>
        </form>

        <aside className="deadline-counter-calendar-card">
          <CalendarSources court={selectedCourt} />
        </aside>
      </div>

      <CounterResult result={result} />
    </div>
  )
}
